"use client";

import { cva } from "class-variance-authority";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/ui/tooltip";
import { cn } from "@/ui/cn";
import { currentEnvironment, environmentLabel } from "@/lib/environment";
import { useAppHost } from "@/lib/hooks/use-app-host";

/**
 * Shared with the About dialog's environment chips, so the two places that name an
 * environment agree on its colour.
 *
 * This badge colours by `displayEnv` (the *data* environment), which is why `preview` was
 * never a key: it is an app-side environment only. The About dialog chips an app and a data
 * environment separately, so it needs one — muted like local, since both are throwaway
 * app environments and the label already distinguishes them.
 */
export const environmentChip = cva(
  "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
  {
    variants: {
      environment: {
        production: "text-destructive-text border-destructive-text/40 bg-destructive-text/10",
        staging: "text-hue-teal border-hue-teal/40 bg-hue-teal/10",
        sandbox: "text-hue-rose border-hue-rose/40 bg-hue-rose/10",
        preview: "text-muted-foreground border-muted-foreground/40 bg-muted-foreground/10",
        local: "text-muted-foreground border-muted-foreground/40 bg-muted-foreground/10",
        unknown: "text-muted-foreground border-muted-foreground/40 bg-muted-foreground/10",
      },
    },
    defaultVariants: { environment: "local" },
  }
);

export function EnvironmentBadge({ className }: { className?: string }) {
  const { appEnv, dataEnv, dataHost, label, showBadge, displayEnv } = currentEnvironment();
  const appHost = useAppHost();

  if (!showBadge) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn(environmentChip({ environment: displayEnv }), className)}>
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="font-mono text-xs">
          <span className="grid grid-cols-[auto_auto] gap-x-3">
            <span>App</span>
            <span>
              {environmentLabel(appEnv).toLowerCase()}
              {appHost && ` · ${appHost}`}
            </span>
            <span>Data</span>
            <span>
              {environmentLabel(dataEnv).toLowerCase()}
              {dataHost && ` · ${dataHost}`}
            </span>
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
